/**
 * BrightPath - ExpenseReportsPage
 * BP-04-04: Expenses Frontend
 */

import React, { useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import {
  FileText,
  Shield,
  PieChart,
  Users,
  Calendar,
  Download,
  ArrowLeft,
} from 'lucide-react';
import {
  useTaxReport,
  useInsuranceReport,
  useCategoryReport,
  useChildReport,
  useExportTaxReport,
} from '../hooks';
import {
  TaxSummaryCard,
  InsuranceClaimTracker,
  ExpenseCategoryChart,
} from '../components';
import { cn } from '@/core/lib/utils';

type ReportTab = 'tax' | 'insurance' | 'category' | 'child';

export function ExpenseReportsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = (searchParams.get('type') as ReportTab) || 'tax';
  const familyId = 'current';

  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState(currentYear);

  const { data: taxReport, isLoading: taxLoading } = useTaxReport(familyId, selectedYear, {
    enabled: activeTab === 'tax',
  });

  const { data: insuranceReport, isLoading: insuranceLoading } = useInsuranceReport(
    familyId,
    {
      startDate: `${selectedYear}-01-01`,
      endDate: `${selectedYear}-12-31`,
    },
    { enabled: activeTab === 'insurance' }
  );

  const { data: categoryReport, isLoading: categoryLoading } = useCategoryReport(
    familyId,
    selectedYear,
    undefined,
    { enabled: activeTab === 'category' }
  );

  const { data: childReport, isLoading: childLoading } = useChildReport(
    familyId,
    selectedYear,
    undefined,
    { enabled: activeTab === 'child' }
  );

  const { mutate: exportTaxReport, isPending: isExporting } = useExportTaxReport();

  const tabs = [
    { id: 'tax' as const, label: 'Tax Report', icon: FileText },
    { id: 'insurance' as const, label: 'Insurance', icon: Shield },
    { id: 'category' as const, label: 'By Category', icon: PieChart },
    { id: 'child' as const, label: 'By Child', icon: Users },
  ];

  const handleTabChange = (tab: ReportTab) => {
    setSearchParams({ type: tab });
  };

  return (
    <div className="min-h-screen bg-cream">
      {/* Header */}
      <div className="bg-white border-b border-warm-gray/10">
        <div className="max-w-6xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-4">
              <Link
                to="/family/expenses"
                className="flex items-center gap-2 text-warm-gray hover:text-navy transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
              </Link>
              <div>
                <h1 className="text-xl font-display font-semibold text-navy">Expense Reports</h1>
                <p className="text-sm text-warm-gray">Generate reports for tax filing and analysis</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(Number(e.target.value))}
                className="px-4 py-2 bg-cream rounded-lg"
              >
                {[currentYear, currentYear - 1, currentYear - 2].map((year) => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex gap-1 overflow-x-auto">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => handleTabChange(tab.id)}
                className={cn(
                  'flex items-center gap-2 px-4 py-2 rounded-t-lg transition-colors whitespace-nowrap',
                  activeTab === tab.id
                    ? 'bg-cream text-navy font-medium'
                    : 'text-warm-gray hover:text-navy'
                )}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-8">
        {/* Tax Report */}
        {activeTab === 'tax' && (
          <div className="space-y-6">
            {taxLoading ? (
              <div className="bg-white rounded-xl p-8 text-center">
                <div className="animate-pulse text-warm-gray">Loading tax report...</div>
              </div>
            ) : taxReport ? (
              <TaxSummaryCard
                report={taxReport}
                onExport={() => exportTaxReport({ familyId, year: selectedYear })}
                isExporting={isExporting}
              />
            ) : (
              <div className="bg-white rounded-xl p-8 text-center text-warm-gray">
                No tax data available for {selectedYear}
              </div>
            )}

            <div className="bg-white rounded-xl border border-warm-gray/10 p-6">
              <h3 className="font-semibold text-navy mb-4">Tips for Tax Filing</h3>
              <ul className="space-y-2 text-warm-gray">
                <li className="flex items-start gap-2">
                  <span className="text-sage">•</span>
                  Medical expenses exceeding 7.5% of AGI may be deductible
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-sage">•</span>
                  Keep all receipts and documentation for verification
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-sage">•</span>
                  Consult with a tax professional for specific advice
                </li>
              </ul>
            </div>
          </div>
        )}

        {/* Insurance Report */}
        {activeTab === 'insurance' && (
          <div>
            {insuranceLoading ? (
              <div className="bg-white rounded-xl p-8 text-center">
                <div className="animate-pulse text-warm-gray">Loading insurance report...</div>
              </div>
            ) : insuranceReport ? (
              <InsuranceClaimTracker report={insuranceReport} />
            ) : (
              <div className="bg-white rounded-xl p-8 text-center text-warm-gray">
                No insurance claims for {selectedYear}
              </div>
            )}
          </div>
        )}

        {/* Category Report */}
        {activeTab === 'category' && (
          <div className="bg-white rounded-xl border border-warm-gray/10 p-6">
            <h2 className="font-semibold text-navy mb-4">Expenses by Category - {selectedYear}</h2>
            {categoryLoading ? (
              <div className="h-[400px] flex items-center justify-center">
                <div className="animate-pulse text-warm-gray">Loading...</div>
              </div>
            ) : categoryReport && categoryReport.categories.length > 0 ? (
              <>
                <ExpenseCategoryChart data={categoryReport.categories} height={400} />
                <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                  {categoryReport.categories.map((cat) => (
                    <div
                      key={cat.category}
                      className="flex items-center justify-between p-4 bg-cream rounded-lg"
                    >
                      <div>
                        <p className="font-medium text-navy">{cat.category}</p>
                        <p className="text-sm text-warm-gray">{cat.count} expenses</p>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold text-navy">${cat.total.toLocaleString()}</p>
                        <p className="text-sm text-warm-gray">{cat.percentage.toFixed(1)}%</p>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <div className="h-[400px] flex items-center justify-center text-warm-gray">
                No expense data for {selectedYear}
              </div>
            )}
          </div>
        )}

        {/* Child Report */}
        {activeTab === 'child' && (
          <div className="bg-white rounded-xl border border-warm-gray/10 p-6">
            <h2 className="font-semibold text-navy mb-4">Expenses by Child - {selectedYear}</h2>
            {childLoading ? (
              <div className="h-[300px] flex items-center justify-center">
                <div className="animate-pulse text-warm-gray">Loading...</div>
              </div>
            ) : childReport && childReport.children.length > 0 ? (
              <div className="space-y-4">
                {childReport.children.map((child) => (
                  <div
                    key={child.childId}
                    className="p-4 bg-cream rounded-lg"
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-sage/20 flex items-center justify-center">
                          <Users className="w-5 h-5 text-sage" />
                        </div>
                        <div>
                          <p className="font-semibold text-navy">{child.childName}</p>
                          <p className="text-sm text-warm-gray">{child.expenseCount} expenses</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-2xl font-bold text-navy">
                          ${child.total.toLocaleString()}
                        </p>
                        <p className="text-sm text-warm-gray">{child.percentage.toFixed(1)}% of total</p>
                      </div>
                    </div>
                    <div className="h-2 bg-white rounded-full overflow-hidden">
                      <div
                        className="h-full bg-sage rounded-full"
                        style={{ width: `${child.percentage}%` }}
                      />
                    </div>
                  </div>
                ))}

                {childReport.unassigned && childReport.unassigned.total > 0 && (
                  <div className="p-4 bg-warm-gray/10 rounded-lg">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-navy">Unassigned</p>
                        <p className="text-sm text-warm-gray">
                          {childReport.unassigned.expenseCount} expenses
                        </p>
                      </div>
                      <p className="font-semibold text-navy">
                        ${childReport.unassigned.total.toLocaleString()}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="h-[300px] flex items-center justify-center text-warm-gray">
                No expense data for {selectedYear}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default ExpenseReportsPage;
